import { useState, useEffect, useMemo } from "react";
import { useApp } from "../context/AppContext";

const STATUS_COLORS = {
  cluster:   "#10b981",
  noSc:      "#f59e0b",
  manual:    "#a855f7",
  notParsed: "#ef4444",
  orphan:    "#06b6d4",
  published: "#16a34a",
  generated: "#3b82f6",
  draft:     "#9ca3af",
};

const PUB_STATUS_LABEL = {
  published: "✅ Опубліковано",
  generated: "📝 Згенеровано",
  manual:    "✏️ Написано вручну",
  draft:     "✏️ Чернетка",
};

// Нормалізація URL для матчингу зі знімком позицій (має збігатися з server/services/positionsGate.js).
const normUrl = (u) => {
  if (!u) return "";
  let s = String(u).trim().toLowerCase().replace(/^https?:\/\//, "").replace(/^www\./, "");
  s = s.split("?")[0].split("#")[0].replace(/\/+$/, "");
  return s;
};

export default function AllPagesMetaView() {
  const { activeId, apiFetch, dark, t, card, inp, btnP, btnG, btnSm, flash, proj } = useApp();

  const [pages, setPages] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [busyUrl, setBusyUrl] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [modalUrl, setModalUrl] = useState(null);
  const [customUrls, setCustomUrls] = useState(() => { try { return JSON.parse(localStorage.getItem('cu_meta_' + activeId) || '[]'); } catch { return []; } });
  const [addUrlInput, setAddUrlInput] = useState('');
  const saveCustomUrls = (arr) => { setCustomUrls(arr); try { localStorage.setItem('cu_meta_' + activeId, JSON.stringify(arr)); } catch {} };
  const handleAddCustomUrl = () => {
    const u = addUrlInput.trim().replace(/\/+$/, '');
    if (!u || !/^https?:\/\//.test(u)) { flash('Введіть повний URL (https://...)'); return; }
    if (customUrls.includes(u)) { flash('Вже є у списку'); setAddUrlInput(''); return; }
    saveCustomUrls([u, ...customUrls]);
    setAddUrlInput('');
    setModalUrl(u);
  };
  const removeCustomUrl = (u) => saveCustomUrls(customUrls.filter(c => c !== u));
  const [sdk, setSdk] = useState(null);
  const [sdkBusy, setSdkBusy] = useState(false);
  const [avgByPage, setAvgByPage] = useState(null); // { hasSnapshot, threshold, byUrl, snapshotDate }
  const [optTasks, setOptTasks] = useState([]);
  const [optBusy, setOptBusy] = useState(false);
  const [optReport, setOptReport] = useState(null); // { task, compare, summary } | null
  const activeOpt = optTasks.find(x => x.status === "active") || null;

  const loadOpt = async () => {
    if (!activeId) return;
    try { const r = await apiFetch(`/api/projects/${activeId}/meta-opt/active`); const d = await r.json(); if (Array.isArray(d)) setOptTasks(d); } catch {}
  };
  useEffect(() => { loadOpt(); }, [activeId]);

  const startOpt = async () => {
    const urls = filtered.map(p => p.url).filter(u => /^https?:\/\//.test(u));
    if (!urls.length) { flash("Немає сторінок у поточному фільтрі"); return; }
    if (!window.confirm(`Створити авто-задачу на ручну оптимізацію мета-даних для ${urls.length} сторінок (поточний фільтр)?\n\nСистема зафіксує поточні позиції (baseline) і створить задачу + клієнтський тікет.`)) return;
    setOptBusy(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/meta-opt/start`, { method: "POST", body: JSON.stringify({ urls }) });
      const d = await r.json();
      if (r.ok) { flash("✓ Авто-задачу створено, baseline зафіксовано"); await loadOpt(); }
      else if (r.status === 409) { flash("⚠ " + (d.message || "Вже є активна задача по цих URL")); await loadOpt(); }
      else flash(d.error || "Помилка");
    } catch (e) { flash("Помилка: " + e.message); }
    setOptBusy(false);
  };
  const finishOpt = async (taskId) => {
    if (!window.confirm("Позначити ручну оптимізацію як завершену? Клієнт отримає повідомлення.")) return;
    setOptBusy(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/meta-opt/${taskId}/finish`, { method: "POST" });
      if (r.ok) { flash("✓ Завершено"); await loadOpt(); } else flash("Помилка завершення");
    } catch (e) { flash("Помилка: " + e.message); }
    setOptBusy(false);
  };
  const loadOptReport = async (taskId) => {
    try {
      const r = await apiFetch(`/api/projects/${activeId}/meta-opt/${taskId}/report`);
      const d = await r.json();
      if (r.ok) setOptReport(d); else flash(d.error || "Помилка звіту");
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const load = async () => {
    if (!activeId) return;
    setLoading(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/meta-gen/all-pages`);
      const d = await r.json();
      if (r.ok) { setPages(d.pages || []); setTotals(d.totals || null); }
      else flash(d.error || "Помилка завантаження");
    } catch (e) { flash("Помилка: " + e.message); }
    setLoading(false);
  };

  const loadSdk = async () => {
    if (!activeId) return;
    setSdkBusy(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/legacy-metago/status`);
      const d = await r.json();
      setSdk(r.ok ? d : { configured: false, error: d.error || "SDK status error" });
    } catch (e) { setSdk({ configured: false, error: e.message }); }
    setSdkBusy(false);
  };

  const loadAvg = async () => {
    if (!activeId) return;
    try {
      const r = await apiFetch(`/api/projects/${activeId}/positions/avg-by-page`);
      const d = await r.json();
      if (r.ok) setAvgByPage(d);
    } catch { /* гейт не критичний для рендеру */ }
  };

  useEffect(() => { load(); loadSdk(); loadAvg(); /* eslint-disable-next-line */ }, [activeId]);

  useEffect(() => {
    const sb = window.innerWidth - document.documentElement.clientWidth;
    if (modalUrl) {
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = sb + "px";
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }
    return () => { document.body.style.overflow = ""; document.body.style.paddingRight = ""; };
  }, [modalUrl]);

  // SDK considered "live" only when key + live checks all pass.
  const sdkLive = !!((sdk?.configured && sdk?.key_valid && sdk?.connection_check) || sdk?.spalah_sdk_connected);

  const gateThreshold = avgByPage?.threshold || 10;
  const avgFor = (url) => avgByPage?.byUrl?.[normUrl(url)]?.avgPos ?? null;

  // Чернетки (є override, але ще не опубліковано) — кандидати на масову публікацію.
  const draftPages = useMemo(
    () => pages.filter(p => p.flags?.hasOverride && !p.flags?.published && /^https?:\/\//.test(p.url)),
    [pages]
  );

  const publishAllDrafts = async () => {
    if (!draftPages.length) { flash("Немає чернеток для публікації"); return; }
    if (!sdkLive) { flash("SDK не підключено — масова публікація недоступна"); return; }
    if (!avgByPage?.hasSnapshot) {
      flash("⚠️ Спочатку зніміть позиції — без знімку публікація заблокована (гейт по позиціях).");
      return;
    }
    const allowed = [], blocked = [];
    for (const p of draftPages) {
      const a = avgFor(p.url);
      if (a != null && a <= gateThreshold) blocked.push(p); else allowed.push(p);
    }
    if (!allowed.length) {
      flash(`Усі ${draftPages.length} чернеток — сторінки в ТОП-${gateThreshold} — публікуйте лише поштучно.`);
      return;
    }
    const msg = `Опублікувати ${allowed.length} чернеток на сайт?`
      + (blocked.length ? `\n\n🔴 ${blocked.length} стор. у ТОП-${gateThreshold} (avg ≤ ${gateThreshold}) ПРОПУЩЕНО — їх мета вже працює, масово не чіпаємо. Лише поштучно й обережно.` : "");
    if (!window.confirm(msg)) return;
    setBulkBusy(true);
    const norm = v => { const s = (v ?? "").trim(); return s ? s : null; };
    let ok = 0, err = 0;
    for (const p of allowed) {
      try {
        const r = await apiFetch(`/api/projects/${activeId}/meta-gen/publish`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url: p.url, title: norm(p.effective?.title), description: norm(p.effective?.description), h1: norm(p.effective?.h1), bulk: true }),
        });
        if (r.ok) ok++; else err++;
      } catch { err++; }
    }
    setBulkBusy(false);
    flash(`✅ Опубліковано ${ok}${err ? `, помилок ${err}` : ""}${blocked.length ? `, пропущено ТОП-${gateThreshold}: ${blocked.length}` : ""}`);
    await load();
  };

  const fetchLive = async (url, e) => {
    if (e) e.stopPropagation();
    setBusyUrl(url);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/site-scan/fetch-one`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });
      const d = await r.json();
      if (r.ok) { flash(`✓ Стягнуто з ${url}`); await load(); }
      else flash(d.error || "Не вдалось стягнути");
    } catch (err) { flash("Помилка: " + err.message); }
    setBusyUrl(null);
  };

  const fetchAllNotParsed = async () => {
    const urls = pages.filter(p => p.flags.notParsed && /^https?:\/\//.test(p.url)).map(p => p.url);
    if (!urls.length) { flash("Немає сторінок для стягування"); return; }
    if (!window.confirm(`Стягнути ${urls.length} сторінок? Може зайняти 1-3 хв.`)) return;
    setBulkBusy(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/site-scan/fetch-bulk`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ urls }),
      });
      const d = await r.json();
      if (r.ok) {
        flash(`✓ Готово: ${d.processed} стягнуто, ${d.failed} помилок з ${d.total}`);
        await load();
      } else flash(d.error || "Помилка bulk-стягування");
    } catch (err) { flash("Помилка: " + err.message); }
    setBulkBusy(false);
  };

  const filtered = useMemo(() => {
    const existUrls = new Set(pages.map(p => p.url.replace(/\/+$/, '')));
    const customPageObjs = customUrls
      .filter(u => !existUrls.has(u))
      .map(u => ({
        url: u, treeName: '', depth: 0, cluster: null, current: {},
        flags: { hasCluster: false, notParsed: false, notInTree: true, noSemantic: true, hasOverride: false, published: false, isCustom: true },
        override: null, effective: { title: '', description: '', h1: '' }
      }));
    let list = filter === 'all' ? [...customPageObjs, ...pages] : pages;
    if (filter === "no_sc") list = list.filter(p => p.flags.noSemantic);
    else if (filter === "manual") list = list.filter(p => p.flags.manuallyEdited);
    else if (filter === "with_cluster") list = list.filter(p => p.flags.hasCluster);
    else if (filter === "not_parsed") list = list.filter(p => p.flags.notParsed);
    else if (filter === "orphan") list = list.filter(p => p.flags.notInTree);
    else if (filter === "published") list = list.filter(p => p.flags.published);
    else if (filter === "draft") list = list.filter(p => !p.flags.published && p.flags.hasOverride);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p => (p.url || "").toLowerCase().includes(q) || (p.effective.title || "").toLowerCase().includes(q));
    }
    return list;
  }, [pages, filter, search]);

  const cellTh = { padding: "8px 10px", fontSize: 11, fontWeight: 600, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.4, textAlign: "left", borderBottom: `1px solid ${t.border}`, background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)" };
  const cellTd = { padding: "10px", fontSize: 12.5, color: t.text, borderBottom: `1px solid ${t.border}`, verticalAlign: "top" };

  const csvEsc = (v) => {
    const s = (v == null ? "" : String(v));
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const exportCsv = () => {
    const rows = [["url", "title", "description", "h1"]];
    for (const p of pages) {
      rows.push([p.url, p.effective?.title || "", p.effective?.description || "", p.effective?.h1 || ""]);
    }
    const csv = "﻿" + rows.map(r => r.map(csvEsc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `meta-${proj?.domain || activeId}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  // Minimal CSV parser (handles quotes, commas, newlines inside quotes)
  const parseCsv = (text) => {
    text = text.replace(/^﻿/, "");
    // Визначаємо роздільник із шапки (tab -> ; -> ,) і ріжемо ЛИШЕ по ньому.
    // Раніше різали і по "," і по ";" одночасно -> коми в Title (в ;-файлах) ламали колонки.
    const firstLine = (text.split(/\r?\n/, 1)[0] || "");
    const sep = firstLine.includes("\t") ? "\t" : firstLine.includes(";") ? ";" : ",";
    const rows = []; let row = []; let cur = ""; let inQ = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (inQ) {
        if (c === '"') { if (text[i + 1] === '"') { cur += '"'; i++; } else inQ = false; }
        else cur += c;
      } else {
        if (c === '"') inQ = true;
        else if (c === sep) { row.push(cur); cur = ""; }
        else if (c === "\n" || c === "\r") { if (c === "\r" && text[i + 1] === "\n") i++; row.push(cur); rows.push(row); row = []; cur = ""; }
        else cur += c;
      }
    }
    if (cur !== "" || row.length) { row.push(cur); rows.push(row); }
    return rows.filter(r => r.some(c => (c || "").trim() !== ""));
  };
  const importCsv = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (rows.length < 2) { flash("Файл порожній або немає рядків даних"); return; }
      const head = rows[0].map(h => h.trim().toLowerCase());
      const iUrl = head.indexOf("url"), iT = head.indexOf("title"), iD = head.indexOf("description"), iH = head.indexOf("h1");
      if (iUrl === -1) { flash("У файлі немає колонки 'url'"); return; }
      if (!window.confirm(`Імпортувати ${rows.length - 1} рядків? Title/Description/H1 перезапишуться вручну для вказаних URL.`)) return;
      setBulkBusy(true);
      let ok = 0, err = 0;
      for (let r = 1; r < rows.length; r++) {
        const url = (rows[r][iUrl] || "").trim();
        if (!url) continue;
        try {
          const resp = await apiFetch(`/api/projects/${activeId}/meta-gen/page-override`, {
            method: "PUT", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              url,
              title: iT >= 0 ? (rows[r][iT] || "") : "",
              description: iD >= 0 ? (rows[r][iD] || "") : "",
              h1: iH >= 0 ? (rows[r][iH] || "") : "",
              source: "manual",
            }),
          });
          if (resp.ok) ok++; else err++;
        } catch { err++; }
      }
      setBulkBusy(false);
      flash(`Імпорт завершено: ${ok} оновлено${err ? `, ${err} помилок` : ""}`);
      await load();
    } catch (e) { setBulkBusy(false); flash("Помилка читання файлу: " + e.message); }
  };

  if (!proj) return null;

  return (
    <div style={{ padding: 20, maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: t.text, letterSpacing: "-0.02em" }}>🔧 Ручна оптимізація</h1>
          <div style={{ fontSize: 12, color: t.textMuted, marginTop: 4 }}>Фінальна правда для сайту: те що тут опубліковано — перекриває AI-формулу і одразу пушиться через SDK.</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {(totals?.notParsed || 0) > 0 && (
            <button
              onClick={fetchAllNotParsed}
              disabled={bulkBusy}
              title="Стягнути title/description/H1 для всіх не спарсених сторінок"
              style={{ ...btnP, height: 32, background: STATUS_COLORS.notParsed, borderColor: STATUS_COLORS.notParsed, color: "#fff", cursor: bulkBusy ? "wait" : "pointer", opacity: bulkBusy ? 0.7 : 1 }}>
              {bulkBusy ? `⏳ Тягну ${totals.notParsed}…` : `📥 Стягнути всі (${totals.notParsed})`}
            </button>
          )}
          <button onClick={exportCsv} disabled={bulkBusy || !pages.length} title="Вивантажити title/description/H1 усіх сторінок у CSV (Excel/Google Sheets)" style={{ ...btnG, height: 32 }}>⬇ Вивантажити CSV</button>
          <label style={{ ...btnG, height: 32, display: "inline-flex", alignItems: "center", cursor: bulkBusy ? "wait" : "pointer", opacity: bulkBusy ? 0.6 : 1 }} title="Завантажити CSV з вручну написаними мета (колонки: url, title, description, h1)">
            ⬆ Завантажити CSV
            <input type="file" accept=".csv,text/csv" disabled={bulkBusy} onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; importCsv(f); }} style={{ display: "none" }} />
          </label>
          {draftPages.length > 0 && (
            <button
              onClick={publishAllDrafts}
              disabled={bulkBusy || !sdkLive || !avgByPage?.hasSnapshot}
              title={!sdkLive ? "SDK не підключено — публікація недоступна"
                : !avgByPage?.hasSnapshot ? "Спочатку зніміть позиції — гейт блокує публікацію без знімку"
                : `Опублікувати всі чернетки. Сторінки в ТОП-${gateThreshold} (avg ≤ ${gateThreshold}) буде пропущено — їх мета працює (лише поштучно)`}
              style={{ ...btnP, height: 32, background: "#16a34a", borderColor: "#16a34a", color: "#fff",
                cursor: (bulkBusy || !sdkLive || !avgByPage?.hasSnapshot) ? "not-allowed" : "pointer",
                opacity: (bulkBusy || !sdkLive || !avgByPage?.hasSnapshot) ? 0.55 : 1 }}>
              {bulkBusy ? "⏳ Публікую…" : `🚀 Опублікувати всі чернетки (${draftPages.length})`}
            </button>
          )}
          <button onClick={load} disabled={bulkBusy} style={{ ...btnG, height: 32 }}>↻ Оновити</button>
          <button onClick={startOpt} disabled={optBusy || !pages.length} title="Створити авто-задачу на ручну оптимізацію мета-даних для сторінок у поточному фільтрі. Зафіксує baseline позицій + створить клієнтський тікет." style={{ ...btnP, height: 32, background: "#6366f1", borderColor: "#6366f1", color: "#fff", cursor: optBusy ? "wait" : "pointer", opacity: optBusy ? 0.7 : 1 }}>{optBusy ? "⏳…" : "🎯 Почати ручну оптимізацію"}</button>
        </div>
      </div>

      {avgByPage && !avgByPage.hasSnapshot && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", marginBottom: 14, borderRadius: 10,
          background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.35)", fontSize: 12.5, color: t.text }}>
          <span style={{ fontSize: 16 }}>⚠️</span>
          <div style={{ flex: 1 }}><strong>Позиції не зняті.</strong> Гейт публікації активний: поки немає знімку позицій, публікація мети заблокована. Зніміть позиції у вкладці «Перевірка позицій».</div>
        </div>
      )}

      {activeOpt && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", marginBottom: 14, borderRadius: 10, background: "rgba(99,102,241,0.10)", border: "1px solid rgba(99,102,241,0.35)", fontSize: 12.5, color: t.text }}>
          <span style={{ fontSize: 18 }}>🎯</span>
          <div style={{ flex: 1 }}>
            <strong>Активна ручна оптимізація мета-даних</strong>
            <span style={{ display: "inline-block", fontSize: 9.5, fontWeight: 800, color: "#fff", background: "#6366f1", borderRadius: 5, padding: "1px 6px", marginLeft: 8, verticalAlign: "middle", letterSpacing: "0.06em" }}>АВТО</span>
            <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>{activeOpt.page_count} сторінок · старт {new Date(activeOpt.started_at).toLocaleDateString("uk-UA")}{(activeOpt.author_full_name || activeOpt.author_name) ? ` · ${activeOpt.author_full_name || (activeOpt.author_name || "").split("@")[0]}` : ""} · baseline зафіксовано</div>
          </div>
          <button onClick={() => loadOptReport(activeOpt.id)} style={{ ...btnG, height: 30 }}>📊 До/після</button>
          <button onClick={() => finishOpt(activeOpt.id)} disabled={optBusy} style={{ ...btnP, height: 30, background: "#16a34a", borderColor: "#16a34a", color: "#fff" }}>✓ Завершити</button>
        </div>
      )}
      {!activeOpt && optTasks.some(x => x.status === "done") && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", marginBottom: 14, borderRadius: 10, background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)", border: `1px solid ${t.border}`, fontSize: 12, color: t.textMuted }}>
          <span>✅</span><div style={{ flex: 1 }}>Остання ручна оптимізація завершена {(() => { const d = optTasks.find(x => x.status === "done"); return d?.finished_at ? new Date(d.finished_at).toLocaleDateString("uk-UA") : ""; })()}.</div>
          <button onClick={() => loadOptReport(optTasks.find(x => x.status === "done").id)} style={{ ...btnG, height: 28 }}>📊 Звіт до/після</button>
        </div>
      )}
      {optReport && (
        <div onClick={() => setOptReport(null)} style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: dark ? "#14141f" : "#fff", border: `1px solid ${t.border}`, borderRadius: 14, maxWidth: 820, width: "100%", maxHeight: "85vh", overflow: "auto", padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: t.text }}>📊 Звіт «до/після» — ручна оптимізація мети</div>
              <button onClick={() => setOptReport(null)} style={{ ...btnG, height: 30 }}>✕</button>
            </div>
            <div style={{ display: "flex", gap: 16, marginBottom: 14, fontSize: 12.5, color: t.textMuted, flexWrap: "wrap" }}>
              <span>Ключів: <strong style={{ color: t.text }}>{optReport.summary.total}</strong></span>
              <span style={{ color: "#16a34a" }}>▲ Покращились: <strong>{optReport.summary.improved}</strong></span>
              <span style={{ color: "#ef4444" }}>▼ Просіли: <strong>{optReport.summary.worsened}</strong></span>
              {optReport.currentSnapshotDate && <span>Поточний знімок: {new Date(optReport.currentSnapshotDate).toLocaleDateString("uk-UA")}</span>}
            </div>
            {optReport.compare.length === 0 ? (
              <div style={{ fontSize: 13, color: t.textMuted, padding: 20, textAlign: "center" }}>Немає ключів з позиціями для порівняння (baseline був порожній).</div>
            ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead><tr style={{ background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)" }}>
                <th style={{ ...cellTh }}>Ключ</th><th style={{ ...cellTh, textAlign: "center" }}>Було</th><th style={{ ...cellTh, textAlign: "center" }}>Стало</th><th style={{ ...cellTh, textAlign: "center" }}>Зміна</th>
              </tr></thead>
              <tbody>
                {optReport.compare.map((cc, i) => (
                  <tr key={i}>
                    <td style={{ ...cellTd }}>{cc.keyword}<div style={{ fontSize: 10, color: t.textMuted }}>{cc.frequency ? cc.frequency.toLocaleString("uk-UA") + " /міс" : ""}</div></td>
                    <td style={{ ...cellTd, textAlign: "center", fontFamily: "monospace" }}>{cc.before ?? "—"}</td>
                    <td style={{ ...cellTd, textAlign: "center", fontFamily: "monospace", fontWeight: 700 }}>{cc.after ?? "—"}</td>
                    <td style={{ ...cellTd, textAlign: "center", fontFamily: "monospace", fontWeight: 700, color: cc.delta == null ? t.textMuted : cc.delta > 0 ? "#16a34a" : cc.delta < 0 ? "#ef4444" : t.textMuted }}>{cc.delta == null ? "—" : cc.delta > 0 ? `▲${cc.delta}` : cc.delta < 0 ? `▼${Math.abs(cc.delta)}` : "="}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </div>
        </div>
      )}
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "10px 14px", marginBottom: 14, borderRadius: 10,
        background: sdkLive ? "rgba(22,163,74,0.08)" : "rgba(239,68,68,0.08)",
        border: `1px solid ${sdkLive ? "rgba(22,163,74,0.30)" : "rgba(239,68,68,0.30)"}`,
        fontSize: 12.5,
      }}>
        <span style={{ fontSize: 18 }}>{sdkLive ? "🟢" : "🔴"}</span>
        <div style={{ flex: 1, color: t.text }}>
          {sdkBusy ? (
            <strong>Перевіряю SDK…</strong>
          ) : sdkLive ? (
            <>
              <strong>SDK підключено</strong>{sdk?.domain ? <> · {sdk.domain}</> : null}
              {sdk?.pages_cached != null && <> · {sdk.pages_cached} сторінок у кеші, {sdk.pages_edited || 0} відредаговано</>}
              {sdk?.last_pulled_at && <> · pull: {new Date(sdk.last_pulled_at).toLocaleString("uk-UA")}</>}
            </>
          ) : !sdk?.configured ? (
            <>
              <strong>SDK не підключено.</strong> Без нього зміни не йдуть на сайт. Налаштуйте у вкладці «Налаштування проєкту».
            </>
          ) : !sdk?.key_valid ? (
            <>
              <strong>SDK key не валідний.</strong> Перевірте ключ або перевстановіть плагін на сайті.
            </>
          ) : (
            <>
              <strong>SDK не відповідає.</strong> Плагін на сайті може бути вимкнений або недоступний.
            </>
          )}
        </div>
        <button onClick={loadSdk} disabled={sdkBusy} style={{ ...btnG, height: 28, fontSize: 11, padding: "0 10px", cursor: sdkBusy ? "wait" : "pointer" }}>↻ Перевірити</button>
      </div>

      {totals && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 16 }}>
          <StatCard label="У структурі" value={totals.inTree ?? totals.structure} color="#6366f1" t={t} />
          <StatCard label="З семантикою" value={totals.withCluster} color={STATUS_COLORS.cluster} t={t} />
          <StatCard label="Без СЯ" value={totals.withoutCluster} color={STATUS_COLORS.noSc} t={t} />
          <StatCard label="Не спарсено" value={totals.notParsed || 0} color={STATUS_COLORS.notParsed} t={t} />
          <StatCard label="Поза структурою" value={totals.notInTree || 0} color={STATUS_COLORS.orphan} t={t} />
          <StatCard label="Ручна правка" value={totals.manuallyEdited} color={STATUS_COLORS.manual} t={t} />
        </div>
      )}

      <div style={{ ...card, padding: "10px 12px", marginBottom: 8, display: "flex", gap: 8, alignItems: "center" }}>
        <span style={{ fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", color: t.textMuted }}>+ Сторінка:</span>
        <input
          value={addUrlInput}
          onChange={e => setAddUrlInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleAddCustomUrl()}
          placeholder="https://example.com/page — вручну"
          style={{ ...inp, fontSize: 12, flex: 1, height: 32 }}
        />
        <button onClick={handleAddCustomUrl} style={{ ...btnP, height: 32, whiteSpace: "nowrap", fontSize: 12 }}>Відкрити</button>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 пошук URL / title…" style={{ ...inp, flex: 1, minWidth: 240, height: 32 }} />
        {[
          ["all", "Усі"], ["with_cluster", "✅ З СЯ"], ["no_sc", "🔍 Без СЯ"],
          ["not_parsed", "⚠️ Не спарсено"], ["orphan", "🌊 Поза структурою"],
          ["published", "🟢 Опубліковано"], ["draft", "📝 Чернетки"],
        ].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} style={{
            ...btnSm,
            background: filter === k ? "#6366f1" : "transparent",
            color: filter === k ? "#fff" : t.textMuted,
            border: `1px solid ${filter === k ? "#6366f1" : t.border}`,
            cursor: "pointer",
          }}>{label}</button>
        ))}
        <span style={{ fontSize: 12, color: t.textMuted, fontFamily: "monospace" }}>{loading ? "..." : `${filtered.length}/${pages.length}`}</span>
      </div>

      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
            <thead>
              <tr>
                <th style={{ ...cellTh, width: "26%" }}>URL</th>
                <th style={{ ...cellTh, width: "10%" }}>СЯ</th>
                <th style={{ ...cellTh, width: "12%" }}>Статус</th>
                <th style={{ ...cellTh, width: "28%" }}>Title</th>
                <th style={{ ...cellTh, width: "20%" }}>Description</th>
                <th style={{ ...cellTh, width: "4%" }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => {
                const cleanUrl = p.url.replace(/^https?:\/\/[^/]+/, "") || "/";
                return (
                  <tr key={p.url}
                      onClick={() => !p.flags.notParsed && setModalUrl(p.url)}
                      style={{
                        cursor: p.flags.notParsed ? "default" : "pointer",
                        background: p.flags.noSemantic ? (dark ? "rgba(245,158,11,0.04)" : "rgba(245,158,11,0.03)") : "transparent",
                        transition: "background .12s",
                      }}
                      onMouseEnter={e => { if (!p.flags.notParsed) e.currentTarget.style.background = dark ? "rgba(99,102,241,0.08)" : "rgba(99,102,241,0.05)"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = p.flags.noSemantic ? (dark ? "rgba(245,158,11,0.04)" : "rgba(245,158,11,0.03)") : "transparent"; }}>
                    <td style={cellTd}>
                      <a href={p.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: t.text, textDecoration: "none", fontSize: 12, wordBreak: "break-all", fontWeight: 600 }}>{cleanUrl}</a>
                      {p.treeName && <div style={{ fontSize: 10, color: t.textMuted, marginTop: 2 }}>{"—".repeat(p.depth || 0)} {p.treeName}</div>}
                      {p.cluster?.hfKeyword && <div style={{ fontSize: 10, color: t.textMuted, marginTop: 3, fontStyle: "italic" }}>🔑 {p.cluster.hfKeyword} {p.cluster.keywordCount ? `(${p.cluster.keywordCount})` : ""}</div>}
                      {p.current.status_code && p.current.status_code !== 200 && <div style={{ fontSize: 10, color: "#ef4444", marginTop: 2, fontWeight: 600 }}>HTTP {p.current.status_code}</div>}
                    </td>

                    <td style={cellTd}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        {p.flags.hasCluster ? <Badge color={STATUS_COLORS.cluster}>✅ З СЯ</Badge> : <Badge color={STATUS_COLORS.noSc}>🔍 Без СЯ</Badge>}
                        {p.flags.notParsed && <Badge color={STATUS_COLORS.notParsed}>⚠️ Не спарс.</Badge>}
                        {p.flags.notInTree && <Badge color={STATUS_COLORS.orphan}>🌊 Orphan</Badge>}
                      </div>
                    </td>

                    <td style={cellTd}>
                      {p.override ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                          {p.override.source === "ai"
                            ? <Badge color={STATUS_COLORS.generated}>📝 Згенеровано</Badge>
                            : <Badge color={STATUS_COLORS.manual}>✏️ Написано вручну</Badge>}
                          {p.override.status === "published"
                            ? <Badge color={STATUS_COLORS.published}>✅ Опубліковано</Badge>
                            : <Badge color={STATUS_COLORS.draft}>📝 Чернетка</Badge>}
                          {p.override.publishedAt && (
                            <div style={{ fontSize: 9.5, color: t.textMuted }}>{new Date(p.override.publishedAt).toLocaleString("uk-UA")}</div>
                          )}
                        </div>
                      ) : <span style={{ fontSize: 11, color: t.textMuted }}>—</span>}
                    </td>

                    <td style={cellTd}>
                      <div style={{ fontSize: 11.5, color: t.text, lineHeight: 1.4, maxHeight: 50, overflow: "hidden" }}>
                        {p.effective.title || <span style={{ color: t.textMuted, fontStyle: "italic" }}>(порожньо)</span>}
                      </div>
                    </td>

                    <td style={cellTd}>
                      <div style={{ fontSize: 11.5, color: t.textMuted, lineHeight: 1.4, maxHeight: 50, overflow: "hidden" }}>
                        {p.effective.description || <span style={{ fontStyle: "italic" }}>(порожньо)</span>}
                      </div>
                    </td>

                    <td style={{ ...cellTd, textAlign: "center" }}>
                      {p.flags.isCustom ? (
                        <button onClick={(e) => { e.stopPropagation(); removeCustomUrl(p.url); }}
                                title="Видалити з ручного списку"
                                style={{ ...btnSm, color: "#ef4444", border: "none", background: "transparent", fontSize: 12, cursor: "pointer", padding: "2px 6px" }}>✕</button>
                      ) : p.flags.notParsed ? (
                        <button onClick={(e) => fetchLive(p.url, e)} disabled={busyUrl === p.url}
                                title="Стягнути title/description/H1 з сайту"
                                style={{ ...btnSm, background: STATUS_COLORS.notParsed, color: "#fff", border: "none", fontSize: 10, padding: "4px 6px", cursor: busyUrl === p.url ? "wait" : "pointer" }}>
                          {busyUrl === p.url ? "..." : "📥"}
                        </button>
                      ) : <span style={{ fontSize: 14, color: t.textMuted }}>→</span>}
                    </td>
                  </tr>
                );
              })}
              {!loading && !filtered.length && (
                <tr><td colSpan={6} style={{ ...cellTd, textAlign: "center", padding: 30, color: t.textMuted }}>
                  {pages.length ? "Нічого не знайдено" : "Спочатку завантажте структуру"}
                </td></tr>
              )}
              {loading && <tr><td colSpan={6} style={{ ...cellTd, textAlign: "center", padding: 30, color: t.textMuted }}>Завантаження…</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {modalUrl && (
        <PageEditorModal
          sdkLive={sdkLive}
          gateInfo={{ hasSnapshot: !!avgByPage?.hasSnapshot, threshold: gateThreshold, avgPos: avgFor(modalUrl) }}
          projectId={activeId}
          url={modalUrl}
          apiFetch={apiFetch}
          flash={flash}
          dark={dark}
          t={t}
          inp={inp}
          btnP={btnP}
          btnG={btnG}
          onClose={() => setModalUrl(null)}
          onSaved={async () => { await load(); }}
        />
      )}
    </div>
  );
}

function Badge({ children, color }) {
  return (
    <span style={{ display: "inline-block", padding: "2px 6px", borderRadius: 5, background: `${color}1f`, color, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3, whiteSpace: "nowrap" }}>{children}</span>
  );
}

function StatCard({ label, value, color, t }) {
  return (
    <div style={{ padding: "10px 12px", background: `${color}12`, border: `1px solid ${color}33`, borderRadius: 10 }}>
      <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color, fontFamily: "monospace", marginTop: 2 }}>{value == null ? "—" : value}</div>
    </div>
  );
}

function PageEditorModal({ projectId, url, apiFetch, flash, dark, t, inp, btnP, btnG, onClose, onSaved, sdkLive, gateInfo }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", description: "", h1: "" });
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await apiFetch(`/api/projects/${projectId}/meta-gen/page-detail?url=${encodeURIComponent(url)}`);
        const d = await r.json();
        if (!alive) return;
        if (r.ok) {
          setDetail(d);
          setForm({
            title: d.effective.title || "",
            description: d.effective.description || "",
            h1: d.effective.h1 || "",
          });
        } else flash(d.error || "Помилка");
      } catch (e) { flash("Помилка: " + e.message); }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [projectId, url, apiFetch, flash]);

  const aiSuggest = async () => {
    setAiBusy(true);
    try {
      const r = await apiFetch(`/api/projects/${projectId}/meta-gen/from-content`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });
      const d = await r.json();
      if (r.ok) {
        setAiResult(d.generated);
        flash("✓ AI запропонував. Перевір і прийми (або відредагуй).");
      } else flash(d.error || "AI помилка");
    } catch (e) { flash("Помилка: " + e.message); }
    setAiBusy(false);
  };

  const acceptAi = async () => {
    if (!aiResult) return;
    const next = {
      title: aiResult.title || form.title,
      description: aiResult.description || form.description,
      h1: aiResult.h1 || form.h1,
    };
    setForm(next);
    // Persist immediately as AI-generated so status badge flips to "📝 Згенеровано" without an extra click.
    try {
      const r = await apiFetch(`/api/projects/${projectId}/meta-gen/page-override`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url,
          title: next.title, description: next.description, h1: next.h1,
          suggestedKeywords: aiResult.suggestedKeywords || null,
          source: "ai",
        }),
      });
      if (r.ok) { flash("✓ Прийнято і збережено як AI-генерацію"); await onSaved(); }
      else { const d = await r.json().catch(() => ({})); flash(d.error || "Не вдалось зберегти"); }
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const saveAndPublish = async () => {
    if (gateInfo && !gateInfo.hasSnapshot) {
      flash("⚠️ Позиції не зняті — публікація заблокована. Зніміть позиції у «Перевірка позицій».");
      return;
    }
    const top = gateInfo && gateInfo.avgPos != null && gateInfo.avgPos <= gateInfo.threshold;
    const baseMsg = "Опублікувати ці зміни на сайт? Title/description/H1 одразу оновляться на живому сайті.";
    const warnMsg = top
      ? `🔴 УВАГА: сторінка в ТОП (середня позиція ${gateInfo.avgPos} ≤ ${gateInfo.threshold}). Мета вже працює і може сильно просісти. Публікувати поштучно?\n\n` + baseMsg
      : baseMsg;
    if (!confirm(warnMsg)) return;
    setSaving(true);
    try {
      // Send null for cleared fields → backend falls back to cluster formula → page_elements live.
      const norm = v => { const s = (v ?? "").trim(); return s ? s : null; };
      const r = await apiFetch(`/api/projects/${projectId}/meta-gen/publish`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url, title: norm(form.title), description: norm(form.description), h1: norm(form.h1) }),
      });
      const d = await r.json();
      if (r.ok) {
        if (d.mode === "published") flash("✅ Опубліковано на сайті!");
        else if (d.mode === "saved_only") flash("✅ Опубліковано. Зміни застосуються на сайті через Spalah SDK автоматично.");
        else flash("✓ Готово");
        await onSaved();
        onClose();
      } else flash(d.error || "Помилка публікації");
    } catch (e) { flash("Помилка: " + e.message); }
    setSaving(false);
  };

  const saveOnly = async () => {
    setSaving(true);
    try {
      const norm = v => { const s = (v ?? "").trim(); return s ? s : null; };
      const r = await apiFetch(`/api/projects/${projectId}/meta-gen/page-override`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url, title: norm(form.title), description: norm(form.description), h1: norm(form.h1), source: "manual" }),
      });
      if (r.ok) {
        flash("💾 Збережено (без публікації)");
        await onSaved();
        onClose();
      } else {
        const d = await r.json().catch(() => ({}));
        flash(d.error || "Помилка");
      }
    } catch (e) { flash("Помилка: " + e.message); }
    setSaving(false);
  };

  const keywords = detail?.cluster?.keywords || [];
  const ngrams = detail?.ngrams || [];
  const usedKw = detail?.usedKeywords || [];
  const suggested = detail?.override?.suggested_keywords || aiResult?.suggestedKeywords || [];

  // Live keyword/ngram usage check against the form (title + description + h1).
  // Updates reactively as the user types or pastes.
  const formText = ((form.title || "") + " " + (form.description || "") + " " + (form.h1 || "")).toLowerCase();
  const isUsedNow = (token) => {
    const tok = (token || "").toString().trim().toLowerCase();
    return tok.length > 1 && formText.includes(tok);
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: dark ? "#1a1a2e" : "#fff",
        border: `1px solid ${t.border}`, borderRadius: 16,
        width: "100%", maxWidth: 900, maxHeight: "90vh", overflowY: "auto",
        padding: 24,
        boxShadow: "0 20px 80px rgba(0,0,0,0.5)",
      }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18, paddingBottom: 14, borderBottom: `1px solid ${t.border}` }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Сторінка</div>
            <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: t.text, textDecoration: "none", fontSize: 14, fontWeight: 600, wordBreak: "break-all", display: "block", marginTop: 2 }}>
              {url.replace(/^https?:\/\/[^/]+/, "") || "/"} ↗
            </a>
            {detail?.cluster?.hfKeyword && (
              <div style={{ fontSize: 12, color: t.textMuted, marginTop: 6 }}>
                🔑 ВЧ-ключ: <strong style={{ color: t.text }}>{detail.cluster.hfKeyword}</strong> · {keywords.length} ключів у СЯ
              </div>
            )}
            {!detail?.cluster && !loading && (
              <div style={{ fontSize: 12, color: STATUS_COLORS.noSc, marginTop: 6, fontWeight: 600 }}>
                🔍 Сторінка без семантичного ядра
              </div>
            )}
          </div>
          <button onClick={onClose} style={{ ...btnG, height: 30 }}>✕ Закрити</button>
        </div>

        {/* Статус гейту по позиціях — окремий блок, щоб було ясно ще до публікації */}
        {gateInfo && (() => {
          const noSnap = !gateInfo.hasSnapshot;
          const avg = gateInfo.avgPos;
          const top = avg != null && avg <= gateInfo.threshold; // в ТОП → захищена
          const cfg = noSnap
            ? { c: "#f59e0b", icon: "⚠️", title: "Позиції не зняті", text: "Публікація заблокована, поки не знято позиції. Зайдіть у «Перевірка позицій» і запустіть перевірку." }
            : avg == null
            ? { c: "#9ca3af", icon: "ℹ️", title: "Немає даних по позиціях цієї сторінки", text: "Для цієї сторінки немає ключів у знятому знімку — гейт неактивний, публікувати можна." }
            : top
            ? { c: "#dc2626", icon: "🔴", title: `Середня позиція: ${avg} (≤ ${gateInfo.threshold}) — сторінка в ТОП`, text: "Мета вже працює — масово міняти НЕ можна. Лише поштучно й обережно: зміна може сильно просісти." }
            : { c: "#16a34a", icon: "🟢", title: `Середня позиція: ${avg} (> ${gateInfo.threshold}) — поза ТОП`, text: "Сторінка ще не в ТОП — мету можна міняти вільно, втрачати нічого." };
          return (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 14px", marginBottom: 16,
              borderRadius: 10, background: `${cfg.c}14`, border: `1px solid ${cfg.c}55` }}>
              <span style={{ fontSize: 18, lineHeight: "20px" }}>{cfg.icon}</span>
              <div style={{ fontSize: 12.5, color: t.text, lineHeight: 1.5 }}>
                <strong>{cfg.title}</strong>
                <div style={{ color: t.textMuted, marginTop: 2 }}>{cfg.text}</div>
              </div>
            </div>
          );
        })()}

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: t.textMuted }}>Завантаження…</div>
        ) : (
          <>
            {/* Editor fields */}
            <Section title="Title" t={t}>
              <textarea value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                        rows={2} style={{ ...inp, fontSize: 13, padding: 10, resize: "vertical", fontFamily: "inherit", width: "100%" }} />
              <CharCounter value={form.title} min={50} max={70} t={t} />
            </Section>

            <Section title="H1" t={t}>
              <input value={form.h1} onChange={e => setForm({ ...form, h1: e.target.value })}
                     style={{ ...inp, fontSize: 13, padding: 10, width: "100%" }} />
            </Section>

            <Section title="Description" t={t}>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                        rows={3} style={{ ...inp, fontSize: 13, padding: 10, resize: "vertical", fontFamily: "inherit", width: "100%" }} />
              <CharCounter value={form.description} min={140} max={180} t={t} />
            </Section>

            {/* AI suggestion (for no-СЯ pages) */}
            {!detail?.cluster && (
              <div style={{ marginTop: 14, padding: 14, background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.3)", borderRadius: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#3b82f6", textTransform: "uppercase", letterSpacing: 0.4 }}>🤖 AI-пропозиція</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={aiSuggest} disabled={aiBusy}
                            style={{ ...btnP, background: "#3b82f6", height: 28, fontSize: 11 }}>
                      {aiBusy ? "..." : (aiResult ? "↻ Ще раз" : "🪄 Запропонуй мета")}
                    </button>
                    {aiResult && (
                      <button onClick={acceptAi} style={{ ...btnP, background: "#10b981", height: 28, fontSize: 11 }}>✓ Прийняти</button>
                    )}
                  </div>
                </div>
                {aiResult && (
                  <div style={{ fontSize: 12, color: t.text, lineHeight: 1.5 }}>
                    <div><strong>Title:</strong> {aiResult.title}</div>
                    <div style={{ marginTop: 4 }}><strong>H1:</strong> {aiResult.h1}</div>
                    <div style={{ marginTop: 4 }}><strong>Description:</strong> {aiResult.description}</div>
                  </div>
                )}
              </div>
            )}

            {/* Keywords in СЯ */}
            {keywords.length > 0 && (
              <Section title={`🎯 Ключі цієї сторінки в СЯ (${keywords.length})`} t={t}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, maxHeight: 120, overflowY: "auto", padding: 6, background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)", borderRadius: 6 }}>
                  {keywords.slice(0, 50).map((k, i) => {
                    const inForm = isUsedNow(k.keyword);
                    const inAuto = usedKw.includes(k.keyword);
                    const lit = inForm || inAuto;
                    return (
                      <span key={i} title={inForm ? "✓ вже у вашій ручній мета" : inAuto ? "✓ в автоматичній мета" : ""} style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        padding: "3px 8px", borderRadius: 12,
                        background: lit ? "rgba(16,185,129,0.18)" : "rgba(99,102,241,0.10)",
                        color: lit ? "#10b981" : t.text,
                        fontSize: 11, fontWeight: 500, border: lit ? "1px solid rgba(16,185,129,0.4)" : "none",
                        transition: "background .12s, color .12s, border-color .12s",
                      }}>
                        {inForm ? "✓ " : ""}{k.keyword}<span style={{ opacity: 0.55, fontSize: 10 }}>×{k.frequency || 1}</span>
                      </span>
                    );
                  })}
                  {keywords.length > 50 && <span style={{ fontSize: 11, color: t.textMuted, padding: "3px 6px" }}>+{keywords.length - 50}</span>}
                </div>
                <div style={{ fontSize: 10.5, color: t.textMuted, marginTop: 4 }}>
                  Зелені — використані (✓ — у вашому ручному тексті). Вписуйте більше ключів у Title / H1 / Description, щоб засвітилось.
                </div>
              </Section>
            )}

            {/* N-grams */}
            {ngrams.length > 0 && (
              <Section title={`📐 N-грами для цієї сторінки (${ngrams.length})`} t={t}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {ngrams.map((n, i) => {
                    const lit = isUsedNow(n.ngram);
                    return (
                      <span key={i} title={lit ? "✓ є у ручній мета" : ""} style={{
                        padding: "3px 8px", borderRadius: 12,
                        background: lit ? "rgba(16,185,129,0.18)" : "rgba(168,85,247,0.10)",
                        color: lit ? "#10b981" : "#a855f7",
                        fontSize: 11, fontWeight: 500,
                        border: lit ? "1px solid rgba(16,185,129,0.4)" : "none",
                        transition: "background .12s, color .12s, border-color .12s",
                      }}>{lit ? "✓ " : ""}{n.ngram} <span style={{ opacity: 0.55, fontSize: 10 }}>×{n.count}</span></span>
                    );
                  })}
                </div>
                <div style={{ fontSize: 10.5, color: t.textMuted, marginTop: 4 }}>Топ 2-3-словні фрази для title/H1. Зелені — вже вписані у вашу мета.</div>
              </Section>
            )}

            {/* AI suggested keys (for no-СЯ pages — saved or just got) */}
            {suggested?.length > 0 && (
              <Section title="🌱 Запропоновані ключі для майбутньої СЯ" t={t}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {suggested.map((s, i) => (
                    <span key={i} style={{
                      padding: "3px 8px", borderRadius: 12,
                      background: "rgba(59,130,246,0.10)", color: "#3b82f6",
                      fontSize: 11, fontWeight: 500,
                    }}>{s.keyword || s} {s.intent && <span style={{ opacity: 0.6, fontSize: 10 }}>· {s.intent}</span>}{s.est_freq && <span style={{ opacity: 0.6, fontSize: 10 }}> · {s.est_freq}</span>}</span>
                  ))}
                </div>
              </Section>
            )}

            {/* Status footer */}
            {detail?.override && (
              <div style={{ marginTop: 14, fontSize: 11.5, color: t.textMuted, fontStyle: "italic", display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                Останнє оновлення: {detail.override.updatedAt ? new Date(detail.override.updatedAt).toLocaleString("uk-UA") : "—"} ·
                {detail.override.source === "ai"
                  ? <Badge color={STATUS_COLORS.generated}>📝 Згенеровано</Badge>
                  : <Badge color={STATUS_COLORS.manual}>✏️ Написано вручну</Badge>}
                {detail.override.status === "published"
                  ? <Badge color={STATUS_COLORS.published}>✅ Опубліковано</Badge>
                  : <Badge color={STATUS_COLORS.draft}>📝 Чернетка</Badge>}
                {detail.override.publishedAt && <span> · {new Date(detail.override.publishedAt).toLocaleString("uk-UA")}</span>}
              </div>
            )}

            {/* Action buttons */}
            <div style={{ marginTop: 20, paddingTop: 14, borderTop: `1px solid ${t.border}`, display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
              <button onClick={onClose} style={{ ...btnG, height: 36, padding: "0 18px" }}>Закрити</button>
              <button onClick={saveOnly} disabled={saving} style={{ ...btnG, height: 36, padding: "0 18px" }}>{saving ? "..." : "💾 Зберегти (без публікації)"}</button>
              {(() => {
                const noSnap = gateInfo && !gateInfo.hasSnapshot;
                const top = gateInfo && gateInfo.avgPos != null && gateInfo.avgPos <= gateInfo.threshold;
                const blocked = !sdkLive || noSnap;
                const label = saving ? "Публікую..."
                  : !sdkLive ? "🔒 SDK off — публікація недоступна"
                  : noSnap ? "🔒 Зніміть позиції"
                  : top ? "🔴 Опублікувати поштучно"
                  : "🚀 Зберегти і опублікувати";
                const title = !sdkLive ? "SDK не підключено або не відповідає — публікація на сайт зараз неможлива"
                  : noSnap ? "Позиції не зняті — публікація заблокована. Зніміть позиції у «Перевірка позицій»."
                  : top ? `Сторінка в ТОП (avg ≤${gateInfo.threshold}) — мета працює, публікуйте поштучно й обережно, може просісти`
                  : "";
                const bg = blocked ? "#9ca3af" : top ? "#dc2626" : "#10b981";
                return (
                  <button onClick={saveAndPublish} disabled={saving || blocked} title={title}
                    style={{ ...btnP, height: 36, padding: "0 22px", background: bg, opacity: blocked ? 0.6 : 1, cursor: blocked ? "not-allowed" : "pointer" }}>
                    {label}
                  </button>
                );
              })()}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, children, t }) {
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );
}

function CharCounter({ value, min, max, t }) {
  const len = (value || "").length;
  const color = len >= min && len <= max ? "#10b981" : len < min ? "#f59e0b" : "#ef4444";
  return (
    <div style={{ fontSize: 10.5, color, fontFamily: "monospace", marginTop: 3, textAlign: "right" }}>
      {len} симв. (опт. {min}–{max})
    </div>
  );
}
